import {
  Box,
  Button,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import {
  type KeyboardEvent,
  type MouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react';

import { type ChipItem, Input, MultiChipInput } from '@revelio/ui';

import type { FilterOperator } from '../../data-builder.store';
import styles from './custom-filter-modal.module.css';
import {
  customFilterInputCssVars,
  customFilterMultiChipInputCssVars,
} from './disabled-input-overrides';
import { RequiredHiddenInput } from './RequiredHiddenInput';
import {
  isEqualityOperator,
  isSelectionListOperator,
} from './string-filter-operators';

type StringFilterValueInputProps = {
  values: string[];
  onChange: (values: string[]) => void;
  operator: FilterOperator | null;
  isDisabled: boolean;
  required?: boolean;
};

function valuesToChipItems(values: string[]): ChipItem[] {
  return values.map((val, index) => ({
    id: `${index}-${val}`,
    label: val,
  }));
}

export function StringFilterValueInput({
  values,
  onChange,
  operator,
  isDisabled,
  required = false,
}: StringFilterValueInputProps) {
  if (isEqualityOperator(operator)) {
    const value = values[0] ?? '';
    return (
      <Input
        size="sm"
        placeholder="Enter value..."
        value={value}
        onChange={(e) => {
          const next = e.target.value;
          onChange(next === '' ? [] : [next]);
        }}
        disabled={isDisabled}
        required={required && !isDisabled}
        cssVars={customFilterInputCssVars}
        data-testid="custom-filter-value-input"
      />
    );
  }

  return (
    <MultiValueChipInput
      values={values}
      onChange={onChange}
      operator={operator}
      isDisabled={isDisabled}
      required={required}
    />
  );
}

function MultiValueChipInput({
  values,
  onChange,
  operator,
  isDisabled,
  required,
}: StringFilterValueInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [editingValues, setEditingValues] = useState<Record<number, string>>(
    {}
  );
  const [isPopoverHovered, setIsPopoverHovered] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const showPopover = !isSelectionListOperator(operator);

  const addValue = (value: string) => {
    onChange([...values, value]);
    setInputValue('');
    onClose();
  };

  useEffect(() => {
    if (!showPopover) {
      if (isOpen) onClose();
      return;
    }
    if (!isOpen && inputValue && isInputFocused) {
      onOpen();
    }
    if (isOpen && !inputValue && isInputFocused) {
      onClose();
    }
  }, [inputValue, isOpen, isInputFocused, onOpen, onClose, showPopover]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || e.repeat || isDisabled || !inputValue) return;

    if (showPopover && isOpen) {
      e.preventDefault();
      addValue(inputValue);
    } else if (!showPopover) {
      e.preventDefault();
      addValue(inputValue);
    }
  };

  const handleAdd = (newValues: string[]) => {
    onChange([...values, ...newValues]);
    setInputValue('');
    onClose();
  };

  const chipItems = valuesToChipItems(values);
  const chipIndexById = new Map(
    chipItems.map((item, index) => [item.id, index] as const)
  );

  const handleRemove = (chipId: string) => {
    const index = chipIndexById.get(chipId);
    if (index === undefined) {
      return;
    }

    onChange(values.filter((_, i) => i !== index));
  };

  return (
    <Box position="relative" data-testid="custom-filter-value-input">
      <RequiredHiddenInput
        isRequired={required === true && !isDisabled}
        hasValue={values.length > 0}
      />
      <Popover
        placement="bottom-start"
        isOpen={isOpen}
        onClose={onClose}
        autoFocus={false}
      >
        <PopoverTrigger>
          <div>
            <MultiChipInput
              ref={wrapperRef}
              items={chipItems}
              onRemove={handleRemove}
              isEditable
              onAdd={handleAdd}
              inputValue={inputValue}
              onInputChange={setInputValue}
              inputRef={inputRef}
              onInputFocus={() => setIsInputFocused(true)}
              onInputBlur={() => {
                setIsInputFocused(false);
                if (isOpen && !isPopoverHovered) {
                  onClose();
                }
              }}
              onInputKeyDown={handleKeyDown}
              disabled={isDisabled}
              cssVars={customFilterMultiChipInputCssVars}
              placeholder="Enter value..."
              size="sm"
              renderChip={(item, remove) => {
                const index = chipIndexById.get(item.id);
                if (index === undefined) {
                  return null;
                }

                const val = item.label;

                return (
                  <EditableChip
                    val={val}
                    index={index}
                    editingValue={editingValues[index]}
                    onEditStart={() => {
                      setEditingValues((prev) => ({ ...prev, [index]: val }));
                    }}
                    onEditChange={(newVal) => {
                      setEditingValues((prev) => ({
                        ...prev,
                        [index]: newVal,
                      }));
                    }}
                    onEditEnd={() => {
                      const newVal = editingValues[index]?.trim();
                      if (newVal) {
                        onChange(
                          values.map((value, currentIndex) =>
                            currentIndex === index ? newVal : value
                          )
                        );
                      } else {
                        onChange(values.filter((_, i) => i !== index));
                      }
                      setEditingValues(
                        ({ [index]: _removed, ...rest }) => rest
                      );
                    }}
                    onRemove={remove}
                  />
                );
              }}
            />
          </div>
        </PopoverTrigger>

        <PopoverContent
          className={styles.valuePopover}
          pointerEvents={isOpen ? 'auto' : 'none'}
          boxShadow="2xl"
          onMouseDown={(e: MouseEvent) => e.preventDefault()}
          sx={{
            ':focus:not(:focus-visible)': {
              shadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25) !important',
            },
          }}
        >
          <PopoverHeader>Set value</PopoverHeader>
          <PopoverBody
            padding={0}
            onMouseOver={() => setIsPopoverHovered(true)}
            onMouseLeave={() => setIsPopoverHovered(false)}
          >
            <Button
              className={styles.valueSuggestionButton}
              w="100%"
              variant="ghost"
              colorScheme="green"
              textColor="text.primary"
              justifyContent="flex-start"
              onClick={() => addValue(`%${inputValue}%`)}
              data-testid="includes-text-button"
              overflowX="hidden"
            >
              includes: <Text color="green">"%{inputValue}%"</Text>
            </Button>

            <Button
              className={styles.valueSuggestionButton}
              w="100%"
              variant="ghost"
              colorScheme="green"
              textColor="text.primary"
              justifyContent="flex-start"
              onClick={() => addValue(`${inputValue}%`)}
              data-testid="starts-with-text-button"
              overflowX="hidden"
            >
              starts with: <Text color="green">"{inputValue}%"</Text>
            </Button>

            <Button
              className={styles.valueSuggestionButton}
              w="100%"
              variant="ghost"
              colorScheme="green"
              textColor="text.primary"
              justifyContent="flex-start"
              onClick={() => addValue(`%${inputValue}`)}
              data-testid="ends-with-text-button"
              overflowX="hidden"
            >
              ends with: <Text color="green">"%{inputValue}"</Text>
            </Button>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
}

function EditableChip({
  val,
  index,
  editingValue,
  onEditStart,
  onEditChange,
  onEditEnd,
  onRemove,
}: {
  val: string;
  index: number;
  editingValue: string | undefined;
  onEditStart: () => void;
  onEditChange: (value: string) => void;
  onEditEnd: () => void;
  onRemove: () => void;
}) {
  const isEditing = editingValue !== undefined;

  return (
    <Flex
      className={styles.editableChip}
      alignItems="center"
      gap="2px"
      bg="var(--ui-light-blue-50)"
      border="1px solid var(--ui-light-blue-500)"
      borderRadius="var(--ui-radius-xs)"
      px={1}
      cursor="pointer"
      data-testid={`custom-filter-value-tag-${index}`}
      onClick={(e) => {
        e.stopPropagation();
        if (!isEditing) {
          onEditStart();
        }
      }}
    >
      {isEditing ? (
        <input
          // biome-ignore lint/a11y/noAutofocus: migrated from develop
          autoFocus
          value={editingValue}
          onChange={(e) => onEditChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onEditEnd();
            }
          }}
          onBlur={onEditEnd}
          style={{
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: 'inherit',
            font: 'inherit',
            width: 'auto',
            minWidth: '30px',
          }}
        />
      ) : (
        <Flex alignItems="center" fontSize="var(--ui-font-size-sm)">
          {val}
        </Flex>
      )}
      {!isEditing && (
        <Box
          as="button"
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            onRemove();
          }}
          display="inline-flex"
          alignItems="center"
          p="2px"
          color="var(--ui-text-muted)"
          _hover={{ color: 'var(--ui-text-normal)' }}
          aria-label={`Remove ${val}`}
        >
          <IconCrossSmallInline />
        </Box>
      )}
    </Flex>
  );
}

function IconCrossSmallInline() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
      <path
        d="M12 4L4 12M4 4L12 12"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
